import { baseBackendUrl, getBackend } from "./baseService";
import { PageUpdate, PageUpdateImage } from "../models/PageUpdate";

export const getPages = async (includeExpired = false) => {
  let url = "/api/pages";
  if (includeExpired) {
    url += "?includeExpired=true";
  }
  const response = await getBackend().get(url);
  return response.data;
};

export const deletePage = async (id: string, token: string) => {
  const response = await getBackend().delete("/api/pages?id=" + id, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return response;
};

export const updatePage = async (page: any, token: string) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  const response = await getBackend().put("/api/pages", page, config);
  return response;
};

export const SPLASH_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const SPLASH_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

// The API returns a path (e.g. /api/pages/{id}/splash?v=3); prefix it with the backend host.
export const getSplashImageSrc = (splashImageUrl?: string) =>
  splashImageUrl ? `${baseBackendUrl}${splashImageUrl}` : undefined;

export const uploadSplashImage = async (pageId: string, file: File, token: string) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await getBackend().post(`/api/pages/${pageId}/splash`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const deleteSplashImage = async (pageId: string, token: string) => {
  const response = await getBackend().delete(`/api/pages/${pageId}/splash`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const VIDEO_MAX_BYTES = 200 * 1024 * 1024;
export const VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"];

// The API returns a path (e.g. /api/pages/{id}/video?v=3); prefix it with the backend host.
export const getVideoSrc = (videoUrl?: string) =>
  videoUrl ? `${baseBackendUrl}${videoUrl}` : undefined;

export const uploadVideo = async (pageId: string, file: File, token: string) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await getBackend().post(`/api/pages/${pageId}/video`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const deleteVideo = async (pageId: string, token: string) => {
  const response = await getBackend().delete(`/api/pages/${pageId}/video`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

export const getPageUpdates = async (pageId: string): Promise<PageUpdate[]> => {
  const response = await getBackend().get("/api/PageUpdates", { params: { pageId } });
  return response.data;
};

export const createPageUpdate = async (update: Pick<PageUpdate, "pageId" | "title" | "body">, token: string) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  const response = await getBackend().post("/api/PageUpdates", update, config);
  return response.data;
};

// Update pictures have the same limits the server enforces for the splash graphic.
export const UPDATE_IMAGE_MAX_BYTES = SPLASH_IMAGE_MAX_BYTES;
export const UPDATE_IMAGE_TYPES = SPLASH_IMAGE_TYPES;

// The API returns a path (e.g. /api/PageUpdates/images/3); prefix it with the backend host.
export const getUpdateImageSrc = (url: string) => `${baseBackendUrl}${url}`;

export const uploadUpdateImage = async (updateId: string, file: File, token: string): Promise<PageUpdateImage> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await getBackend().post(`/api/PageUpdates/${updateId}/images`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const deletePageUpdate = async (id: string, token: string) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: { id },
  };

  const response = await getBackend().delete("/api/PageUpdates", config);
  return response;
};
